import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Stack,
    Typography,
} from "@mui/joy"
import React, {useState, useCallback} from "react"
import {ArrowLeft, Eye, EyeOff, LockIcon, LucideMail} from "lucide-react"
import {Link, useNavigate} from "react-router-dom"
import {motion} from "motion/react"
import {useAuth} from "../hooks/useAuth"
import {useUITheme} from "../hooks/useUITheme"
import GuestNavbar from "../components/Layout/GuestNavbar"
import {useTranslation} from "react-i18next";

const Login = () => {
    const uiTheme = useUITheme()
    const auth = useAuth()
    const {t} = useTranslation()
    const [credentials, setCredentials] = useState<{ email?: string, password?: string }>({})
    const [passwordVisible, setPasswordVisible] = useState(false)

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        auth.login(credentials as { email: string, password: string })
    }

    const handleCredentialsChange = useCallback((key: "email" | "password", value: string) => {
        setCredentials((prev) => ({...prev, [key]: value}))
    }, [])

    const togglePasswordVisibility = useCallback(() => {
        setPasswordVisible((v) => !v)
    }, [])

    return (
        <Stack sx={{overflowX: "hidden"}}>

            <Stack paddingX={{xs: 2, sm: 3}} justifyContent={"center"} gap={2} alignItems={"center"} paddingY={3}
                   sx={{backgroundColor: "background.surface"}}>

                {/* The wrapper needs its own width: without it the card sizes to its content and
                    overflows narrow screens. */}
                <motion.div
                    style={{width: "100%", maxWidth: "35rem"}}
                    initial={{opacity: 0, y: 16}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.4}}>
                    <Stack gap={2}>

                        <Card variant={"outlined"}
                              sx={{
                                  paddingX: {xs: 2.5, sm: 5},
                                  alignItems: "center",
                                  gap: 1,
                                  width: "100%",
                                  boxSizing: "border-box",
                                  paddingY: 4
                              }}>

                            <Box component={"img"}
                                 width={"10rem"}
                                 height={"auto"}
                                 src={uiTheme.fullLogo}
                                 alt={'LinguaScore Logo'}
                            />

                            <Stack gap={1} alignItems="center" width="100%">
                                <Typography
                                    level="h2"
                                    textAlign="center">
                                    {t("loginTitle")}
                                </Typography>
                                <Typography
                                    level="body-md"
                                    textColor="neutral.600"
                                    paddingX={{xs: 0, sm: "10%"}}
                                    textAlign="center">
                                    {t("loginDescription")}
                                </Typography>
                            </Stack>

                            <Box component="form" onSubmit={handleSubmit} sx={{width: "100%", maxWidth: "22rem"}}>
                                <Stack gap={2} paddingY={1}>
                                    <FormControl>
                                        <FormLabel>{t("emailFieldLabel")}</FormLabel>
                                        <Input
                                            size="lg"
                                            startDecorator={<LucideMail size={20}/>}
                                            placeholder={t("emailFieldPlaceholder")}
                                            type="email"
                                            value={credentials.email ?? ""}
                                            onChange={(e) => handleCredentialsChange("email", e.target.value)}
                                            sx={{"--Input-focusedThickness": "2px"}}
                                        />
                                    </FormControl>

                                    <Stack gap={1}>
                                        <FormControl>
                                            <FormLabel>{t("passwordFieldLabel")}</FormLabel>
                                            <Input
                                                size="lg"
                                                startDecorator={<LockIcon size={20}/>}
                                                endDecorator={<IconButton
                                                    type="button" variant="plain"
                                                    color="neutral"
                                                    aria-label={passwordVisible ? "Hide password" : "Show password"}
                                                    onClick={togglePasswordVisibility}>
                                                    {passwordVisible ? <EyeOff size={20}/> : <Eye size={20}/>}
                                                </IconButton>}
                                                placeholder={t("currentPasswordFieldPlaceholder")}
                                                type={passwordVisible ? "text" : "password"}
                                                value={credentials.password ?? ""}
                                                onChange={(e) => handleCredentialsChange("password", e.target.value)}
                                                sx={{"--Input-focusedThickness": "2px"}}
                                            />
                                        </FormControl>

                                        <Link
                                            to="/reset-password"
                                            style={{
                                                alignSelf: "flex-end",
                                                textDecoration: "none",
                                                fontSize: "var(--joy-fontSize-sm)",
                                            }}>
                                            <Typography level="body-sm" sx={{color: "primary.500"}}>
                                                {t("callToResetPasswordActionLabel")}
                                            </Typography>
                                        </Link>
                                    </Stack>


                                    <Button
                                        type="submit"
                                        size="lg"
                                        fullWidth
                                        loading={auth.loading}
                                        disabled={!credentials.email || !credentials.password}>
                                        {t("loginActionLabel")}
                                    </Button>
                                </Stack>
                            </Box>

                            <Stack gap={2} width={"100%"} maxWidth={"22rem"}>
                                <Button color={"danger"} variant={"outlined"}
                                        sx={{borderRadius: 30}}
                                        onClick={() => auth.authenticateWithProvider("google")}
                                        startDecorator={<Box component={'img'}
                                                             src={require("../assets/google_logo.png")}
                                                             sx={{width: 30}}/>} size={"lg"}>
                                    {t("googleLoginActionLabel")}
                                </Button>

                                <Button size={"lg"} variant={"outlined"}
                                        sx={{borderRadius: 30}}
                                        onClick={() => auth.authenticateWithProvider("facebook")}
                                        color={"secondary"}
                                        startDecorator={<Box component={'img'}
                                                             src={require("../assets/facebook_logo.webp")}
                                                             sx={{width: 30}}/>}>
                                    {t("facebookLoginActionLabel")}
                                </Button>
                            </Stack>

                            <Typography level="body-sm" textAlign="center" sx={{marginTop: 2}}>
                                {t("callToRegisterActionLabel")}{" "}
                                <Link to="/register" style={{textDecoration: "none"}}>
                                    <Typography
                                        component="span"
                                        level="title-sm"
                                        sx={{color: "primary.500", fontWeight: 600}}>
                                        {t("registerActionLabel")}
                                    </Typography>
                                </Link>
                            </Typography>
                        </Card>

                    </Stack>
                </motion.div>
            </Stack>
        </Stack>
    )
}

export default Login
